import { useEffect, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import Youtube from "@tiptap/extension-youtube";
import { Bold, Italic, Underline as UnderlineIcon, Heading2, Heading3, List, ListOrdered, Quote, Link2, ImagePlus, Video, Youtube as YoutubeIcon, Minus, Undo2, Redo2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, resolveImage } from "@/lib/api";
import { VideoNode } from "@/components/editor/VideoNode";

function ToolBtn({ onClick, active, label, children, testid, disabled }) {
  return (
    <button type="button" onClick={onClick} title={label} aria-label={label} data-testid={testid} disabled={disabled}
      className={`rounded-md p-2 transition-colors disabled:opacity-50 ${active ? "bg-[var(--chakra)] text-white" : "text-stone-600 hover:bg-[var(--saffron-tint)] hover:text-[var(--saffron-deep)]"}`}>
      {children}
    </button>
  );
}

export function RichTextEditor({ value, onChange, placeholder = "Start writing your story for India... (you can paste or drop pictures and videos right here)" }) {
  const fileRef = useRef(null);
  const videoRef = useRef(null);
  const [videoProgress, setVideoProgress] = useState(null);
  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/admin/upload", fd, { timeout: 0 });
    return data;
  };

  const insertFiles = async (ed, files) => {
    for (const file of files) {
      if (!file.type.startsWith("image/") && !file.type.startsWith("video/")) continue;
      if (file.type.startsWith("video/") && file.size > 200 * 1024 * 1024) { toast.error("Video must be under 200 MB"); continue; }
      try {
        const data = await uploadFile(file);
        if (data.kind === "video") ed.chain().focus().setVideo({ src: resolveImage(data.url) }).run();
        else ed.chain().focus().setImage({ src: resolveImage(data.url), alt: file.name }).run();
        toast.success(data.kind === "video" ? "Video inserted" : "Image inserted");
      } catch (err) {
        toast.error(apiError(err, "Upload failed"));
      }
    }
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ link: { openOnClick: false, autolink: true } }),
      Image.configure({ inline: false }),
      Youtube.configure({ controls: true, nocookie: true, width: 640, height: 360 }),
      VideoNode,
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    immediatelyRender: false,
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: { "data-testid": "rich-text-editor" },
      handlePaste: (view, event) => {
        const files = Array.from(event.clipboardData?.files || []).filter((f) => f.type.startsWith("image/") || f.type.startsWith("video/"));
        if (!files.length) return false;
        event.preventDefault();
        insertFiles(editorRef.current, files);
        return true;
      },
      handleDrop: (view, event) => {
        const files = Array.from(event.dataTransfer?.files || []).filter((f) => f.type.startsWith("image/") || f.type.startsWith("video/"));
        if (!files.length) return false;
        event.preventDefault();
        const pos = view.posAtCoords({ left: event.clientX, top: event.clientY });
        if (pos) editorRef.current?.commands.setTextSelection(pos.pos);
        insertFiles(editorRef.current, files);
        return true;
      },
    },
  });
  const editorRef = useRef(null);
  editorRef.current = editor;

  useEffect(() => {
    if (editor && value !== undefined && editor.getHTML() !== value && !editor.isFocused) {
      editor.commands.setContent(value || "", { emitUpdate: false });
    }
  }, [value, editor]);

  if (!editor) return <div className="h-96 animate-pulse rounded-2xl bg-white/80" />;

  const setLink = () => {
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("Link URL", prev || "https://");
    if (url === null) return;
    if (url === "") return editor.chain().focus().extendMarkRange("link").unsetLink().run();
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const addYoutube = () => {
    const url = window.prompt("YouTube video URL", "https://www.youtube.com/watch?v=");
    if (!url) return;
    const ok = editor.commands.setYoutubeVideo({ src: url });
    if (!ok) toast.error("That doesn't look like a YouTube link");
  };

  const uploadInline = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    await insertFiles(editor, [file]);
  };

  const uploadInlineVideo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 200 * 1024 * 1024) return toast.error("Video must be under 200 MB");
    const fd = new FormData();
    fd.append("file", file);
    setVideoProgress(0);
    try {
      const { data } = await api.post("/admin/upload", fd, {
        timeout: 0,
        onUploadProgress: (ev) => setVideoProgress(ev.total ? Math.round((ev.loaded / ev.total) * 100) : 0),
      });
      editor.chain().focus().setVideo({ src: resolveImage(data.url) }).run();
      toast.success("Video inserted");
    } catch (err) {
      toast.error(apiError(err, "Video upload failed"));
    } finally {
      setVideoProgress(null);
    }
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-[var(--line)] bg-white" data-testid="editor-wrapper">
      <div className="flex flex-wrap items-center gap-1 border-b border-[var(--line)] bg-[var(--paper)] p-2">
        <ToolBtn testid="editor-bold" label="Bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><Bold className="h-4 w-4" /></ToolBtn>
        <ToolBtn testid="editor-italic" label="Italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic className="h-4 w-4" /></ToolBtn>
        <ToolBtn testid="editor-underline" label="Underline" active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()}><UnderlineIcon className="h-4 w-4" /></ToolBtn>
        <span className="mx-1 h-6 w-px bg-[var(--line)]" />
        <ToolBtn testid="editor-h2" label="Heading" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 className="h-4 w-4" /></ToolBtn>
        <ToolBtn testid="editor-h3" label="Subheading" active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><Heading3 className="h-4 w-4" /></ToolBtn>
        <ToolBtn testid="editor-bullets" label="Bullet list" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}><List className="h-4 w-4" /></ToolBtn>
        <ToolBtn testid="editor-ordered" label="Numbered list" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered className="h-4 w-4" /></ToolBtn>
        <ToolBtn testid="editor-quote" label="Quote" active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()}><Quote className="h-4 w-4" /></ToolBtn>
        <ToolBtn testid="editor-hr" label="Divider" onClick={() => editor.chain().focus().setHorizontalRule().run()}><Minus className="h-4 w-4" /></ToolBtn>
        <span className="mx-1 h-6 w-px bg-[var(--line)]" />
        <ToolBtn testid="editor-link" label="Link" active={editor.isActive("link")} onClick={setLink}><Link2 className="h-4 w-4" /></ToolBtn>
        <ToolBtn testid="editor-image" label="Insert image" onClick={() => fileRef.current?.click()}><ImagePlus className="h-4 w-4" /></ToolBtn>
        <ToolBtn testid="editor-video" label="Insert video from computer" disabled={videoProgress !== null} onClick={() => videoRef.current?.click()}><Video className="h-4 w-4" /></ToolBtn>
        <ToolBtn testid="editor-youtube" label="Embed YouTube video" onClick={addYoutube}><YoutubeIcon className="h-4 w-4" /></ToolBtn>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadInline} data-testid="editor-image-input" />
        <input ref={videoRef} type="file" accept="video/mp4,video/webm,video/quicktime,video/ogg" className="hidden" onChange={uploadInlineVideo} data-testid="editor-video-input" />
        <span className="mx-1 h-6 w-px bg-[var(--line)]" />
        <ToolBtn testid="editor-undo" label="Undo" onClick={() => editor.chain().focus().undo().run()}><Undo2 className="h-4 w-4" /></ToolBtn>
        <ToolBtn testid="editor-redo" label="Redo" onClick={() => editor.chain().focus().redo().run()}><Redo2 className="h-4 w-4" /></ToolBtn>
        {videoProgress !== null && (
          <span className="ml-2 text-xs font-medium text-[var(--india-green)]" data-testid="editor-video-progress">Uploading video… {videoProgress}%</span>
        )}
        <span className="ml-auto hidden text-[11px] text-stone-400 lg:inline">Tip: paste (Ctrl+V) or drop pictures & videos straight into the text</span>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
